import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, MessageCircle, ArrowLeft } from "lucide-react";
import Avatar from "@/components/app/Avatar";
import PresenceDot from "@/components/app/PresenceDot";
import { useRealtime } from "@/hooks/useRealtime";

export default function ClientMessages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const preselectMember = loc.state?.member || null;

  const [me, setMe] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: meData }, { data: msgsData }] = await Promise.all([
        api.get("/client/me"),
        api.get("/client/messages"),
      ]);
      setMe(meData);
      setMsgs(msgsData);
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      }, 100);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load messages");
    }
  };
  useEffect(() => { load(); }, []);

  // Realtime — reload on any message event
  useRealtime(user?.role === "client", (evt) => {
    if (evt.event === "message_sent" || evt.event === "message_received") load();
  });

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post("/client/messages", { text: t });
      setText("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  // Always show Irving Muniz as Manager at the top of the team roster.
  // If the backend already returned him (with an avatar), pin + relabel him
  // to "Manager"; otherwise prepend a static entry so the client always sees
  // Irving as their point of contact.
  const teamWithPhotos = React.useMemo(() => {
    const all = (me?.team || []).slice();
    const isIrving = (t) =>
      (t?.email || "").toLowerCase() === "irving@creditquack.com" ||
      /irving/i.test(t?.first_name || "") ||
      /muniz/i.test(t?.name || "");
    let irving = all.find(isIrving);
    const rest = all.filter((t) => !isIrving(t) && t.avatar_url);
    if (irving) {
      irving = {
        ...irving,
        title: "Manager",
        first_name: irving.first_name || "Irving",
        name: irving.name || "Irving Muniz",
        avatar_url: irving.avatar_url || "https://customer-assets-jt897jd0.emergentagent.net/job_quack-dashboard-pro/artifacts/f915nzal_RNI-Films-IMG-B57D90A2-EFAD-4CA4-ABDC-819844D71FCA.webp",
      };
    } else {
      irving = {
        id: "irving-muniz-manager",
        name: "Irving Muniz",
        first_name: "Irving",
        title: "Manager",
        avatar_url: "https://customer-assets-jt897jd0.emergentagent.net/job_quack-dashboard-pro/artifacts/f915nzal_RNI-Films-IMG-B57D90A2-EFAD-4CA4-ABDC-819844D71FCA.webp",
        last_seen_at: null,
      };
    }
    return [irving, ...rest];
  }, [me?.team]);

  return (
    <AppShell>
      <div className="cq-in max-w-5xl">
        <button
          onClick={() => nav("/portal")}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-800 mb-2"
          data-testid="back-to-portal"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </button>
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Client portal</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">Messages</h1>
        <p className="text-slate-600 mt-1">Direct line to your Credit Quack team.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Team sidebar */}
          <Card className="lg:col-span-1 p-5 rounded-2xl border-slate-100 h-fit" data-testid="messages-team-panel">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Your team</p>
            <div className="space-y-3">
              {teamWithPhotos.length === 0 && <p className="text-sm text-slate-400">Loading team…</p>}
              {teamWithPhotos.map((t) => (
                <div
                  key={t.id}
                  className={`flex items-center gap-3 rounded-2xl border p-3 transition ${
                    preselectMember?.id === t.id ? "border-amber-300 bg-amber-50/50" : "border-slate-100 hover:border-slate-200"
                  }`}
                  data-testid={`msg-team-${(t.first_name || t.name || "").toLowerCase()}`}
                >
                  <div className="relative">
                    <Avatar src={t.avatar_url} name={t.name} size="lg" tone="amber" ringed objectPosition="center 25%" />
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ${
                        isOnline(t.last_seen_at) ? "bg-emerald-500 animate-pulse" : "bg-slate-300"
                      }`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-black text-slate-900 leading-tight">{t.first_name || t.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{t.title || "Specialist"}</p>
                    <PresenceDot lastSeenAt={t.last_seen_at} className="mt-1" />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
              Messages are shared with your assigned team — everyone here can respond so you're never waiting on one person.
            </p>
          </Card>

          {/* Chat */}
          <Card className="lg:col-span-2 p-5 rounded-2xl border-slate-100 flex flex-col h-[70vh]" data-testid="messages-chat-panel">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center">
                <MessageCircle size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-slate-900">Team chat</p>
                <p className="text-[11px] text-slate-500">Auto-refreshed live</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1 space-y-3 bg-slate-50 rounded-xl p-3">
              {msgs.length === 0 && (
                <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="messages-empty">
                  No messages yet — say hi 👋
                </div>
              )}
              {msgs.map((m) => {
                const mine = m.sender_role === "client";
                return (
                  <div key={m.id} className={`flex gap-2 items-end ${mine ? "flex-row-reverse" : "flex-row"}`} data-testid={`msg-${m.id}`}>
                    <Avatar src={m.sender_avatar_url} name={m.sender_name} size="sm" tone={mine ? "sky" : "amber"} ringed />
                    <div className={`max-w-[75%] rounded-2xl p-3 text-sm shadow-sm ${mine ? "bg-amber-100 text-amber-900 rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                      <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                      <p className="text-[10px] opacity-70 mt-1 font-semibold">
                        {m.sender_name}
                        {m.sender_title && !mine ? ` · ${m.sender_title}` : ""}
                        {" · "}
                        {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 flex gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder="Type a message and press Enter…"
                data-testid="msg-input"
              />
              <Button onClick={send} disabled={busy || !text.trim()} className="bg-amber-500 hover:bg-amber-600" data-testid="msg-send">
                <Send size={14} className="mr-1" /> Send
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function isOnline(lastSeenAt) {
  if (!lastSeenAt) return false;
  try {
    return (Date.now() - new Date(lastSeenAt).getTime()) < 5 * 60 * 1000;
  } catch { return false; }
}
